/**
 * Persist agent chat sessions per ERP module (localStorage).
 */

import { getBrandLocale } from '../../shared/utils/brandCurrency';
import i18n from '../../i18n';

const ss = (key, opts) => i18n.t(`modules.shared.aiAgent.sessionStorage.${key}`, opts);
const cp = (key, opts) => i18n.t(`modules.shared.aiAgent.chat.${key}`, opts);

const STORAGE_PREFIX = 'qawaem_agent_sessions_v1';
const MAX_SESSIONS = 20;

export const AGENT_SESSION_STATUS = {
  IN_PROGRESS: 'in_progress',
  PRIVATE: 'private',
  SHARED: 'shared',
};

export function resolveSessionDisplayStatus(session, activeSessionId) {
  if (session?.id === activeSessionId) {
    return AGENT_SESSION_STATUS.IN_PROGRESS;
  }
  if (session?.shared) {
    return AGENT_SESSION_STATUS.SHARED;
  }
  return AGENT_SESSION_STATUS.PRIVATE;
}

function storageKey(moduleId) {
  return `${STORAGE_PREFIX}:${moduleId || 'facturation'}`;
}

function serializeEntry(entry) {
  if (!entry) return null;
  return {
    kind: entry.kind,
    text: entry.text,
    attachmentKind: entry.attachmentKind,
    imageName: entry.imageName,
    plan: entry.plan || null,
    executionResult: entry.executionResult
      ? {
          success: entry.executionResult.success,
          message: entry.executionResult.message,
        }
      : null,
  };
}

export function serializeEntries(entries = []) {
  return entries.map(serializeEntry).filter(Boolean);
}

export function deriveSessionTitle(entries = []) {
  const firstPrompt = entries.find((entry) => entry.kind === 'prompt');
  const raw = String(firstPrompt?.text || '').trim();
  if (!raw || raw === ss('attached_document_legacy') || raw === cp('attached_prompt')) {
    return ss('untitled');
  }
  if (raw.length <= 52) return raw;
  return `${raw.slice(0, 52)}…`;
}

export function formatSessionActivity(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const day = date.toLocaleDateString(getBrandLocale(), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const time = date.toLocaleTimeString(getBrandLocale(), {
    hour: '2-digit',
    minute: '2-digit',
  });
  return ss('date_at', { day, time });
}

export function loadAgentSessions(moduleId) {
  try {
    const raw = localStorage.getItem(storageKey(moduleId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return hydrateStoredSessions(Array.isArray(parsed) ? parsed : []);
  } catch {
    return [];
  }
}

export function saveAgentSessions(moduleId, sessions) {
  try {
    localStorage.setItem(storageKey(moduleId), JSON.stringify(sessions.slice(0, MAX_SESSIONS)));
  } catch {
    /* ignore quota errors */
  }
}

export function createAgentSession({
  moduleId,
  title,
  owner,
  entries = [],
  shared = false,
}) {
  const now = new Date().toISOString();
  const session = {
    id: `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    title: title || deriveSessionTitle(entries),
    shared: !!shared,
    owner: owner || ss('default_owner'),
    createdAt: now,
    updatedAt: now,
    entries: serializeEntries(entries),
  };

  const sessions = [session, ...loadAgentSessions(moduleId)].slice(0, MAX_SESSIONS);
  saveAgentSessions(moduleId, sessions);
  return session;
}

export function upsertAgentSession(moduleId, sessionId, patch) {
  const sessions = loadAgentSessions(moduleId);
  const index = sessions.findIndex((session) => session.id === sessionId);
  if (index === -1) return null;

  const updated = {
    ...sessions[index],
    ...patch,
    updatedAt: patch.updatedAt || new Date().toISOString(),
  };
  sessions[index] = updated;
  saveAgentSessions(moduleId, sessions);
  return updated;
}

export function deleteAgentSession(moduleId, sessionId) {
  const sessions = loadAgentSessions(moduleId).filter((session) => session.id !== sessionId);
  saveAgentSessions(moduleId, sessions);
  return sessions;
}

export function hydrateStoredSessions(sessions = []) {
  return sessions.map((session) => ({
    ...session,
    shared: !!session.shared,
  }));
}
